import { ReactNode, SyntheticEvent, useRef } from 'react';
import { PersonAPI, PersonHelpers } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import { removeNonDigits } from '@frontend/phone-numbers';
import { createPlaceCallEvent, useQueueStore } from '@frontend/queue';
import { ModalControlTriggerProps, useModalControl } from '@frontend/design-system';
import { ClickToCallSelection } from './selection-modal/click-to-call-selection';
import { CallSelectionContext } from './types';

const queryKeys = {
  base: ['calls'],
  outboundNumbers: (tenantId: string) => [...queryKeys.base, 'outboundVoiceNumbers', tenantId],
};

type UsePhoneCallActionArgs = {
  context: CallSelectionContext;
};

type UsePhoneCallActionReturnTypes = {
  triggerProps?: Omit<ModalControlTriggerProps, 'onClick'> & {
    onClick: (e?: SyntheticEvent, overrideNumber?: string) => void;
  };
  closeModal: () => void;
  Modal: ReactNode;
  disabled: boolean;
  disabledDetails: string;
  multipleContactNumbers: ReturnType<typeof PersonHelpers.getAllPhoneNumbers>;
};

// Case 1: if personNumber is passed in the context, that means we don't need to give options to select from contact numbers
// Case 2: if person data is passed from person profile or contact page, then provide options to select from various contact numbers
// Case 3: if person Id is passed, then fetch the personData and provide options to select from various contact numbers
// Also, if outbound numbers are multiple then show the modal where we provide options to select the outbound numbers

export const usePhoneCallAction = ({ context }: UsePhoneCallActionArgs): UsePhoneCallActionReturnTypes => {
  const { t } = useTranslation('phone-call');
  const modal = useModalControl();
  const { phoneConfig } = usePhoneConfigShallowStore('phoneConfig');
  const numToRef = useRef('');

  let disabledDetails = '';

  const { data: personData, isLoading: isPersonDataLoading } = PersonAPI.usePersonExtended(
    {
      personId: context?.personId ?? '',
      locationId: context?.locationId ?? '',
    },
    {
      enabled: !context?.person && !!context?.personId,
    }
  );

  const person = context.person || personData;

  const multipleContactNumbers = PersonHelpers.getAllPhoneNumbers(person) ?? [];
  const requiresContactNumberSelection = !context.phoneNumber && !!person && !(multipleContactNumbers.length === 1);

  const dialNumber = removeNonDigits(context.phoneNumber || multipleContactNumbers[0]?.number);

  const { triggerDialpad } = useDialpadTrigger();
  const handleClick = (e?: SyntheticEvent, overrideNumber?: string) => {
    const numToDial = overrideNumber ?? dialNumber;
    e?.stopPropagation();
    if (!!phoneConfig && (!requiresContactNumberSelection || !!overrideNumber)) {
      triggerDialpad(numToDial, context.outboundNumber);
      modal.closeModal();
    } else {
      numToRef.current = numToDial;
      modal.triggerProps.onClick();
    }
  };

  if (!context?.personId && !context?.phoneNumber && (!context.person || multipleContactNumbers.length === 0)) {
    disabledDetails = t('This contact has no associated phone numbers.');
  }

  return {
    triggerProps: !!disabledDetails
      ? undefined
      : {
          ...modal.triggerProps,
          onClick: (e, overrideNumber) => handleClick(e, overrideNumber),
        },
    closeModal: modal.closeModal,
    Modal: (
      <ClickToCallSelection
        modal={modal}
        isLoading={isPersonDataLoading}
        triggerDialpad={() => {
          triggerDialpad(numToRef.current, context.outboundNumber);
        }}
      />
    ),
    multipleContactNumbers,
    disabled: !!disabledDetails,
    disabledDetails: disabledDetails,
  };
};

export const useDialpadTrigger = () => {
  const { enqueue } = useQueueStore.getState();

  const triggerDialpad = (dialNumber: string, specificOutboundNumber?: string) => {
    // Use the event creator from the queue module
    const event = createPlaceCallEvent({
      dialNumber,
      outboundNumber: specificOutboundNumber,
    });

    enqueue('dialpad', {
      ...event,
      waitForHandler: true,
    });
  };

  return { triggerDialpad };
};
